import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const APPLICATION_SEARCH_RESOURCE = "application" as const;
export const APPLICATION_SEARCH_OPERATION = "search" as const;
export const APPLICATION_SEARCH_API_PATH =
  `/v1/${APPLICATION_SEARCH_RESOURCE}/${APPLICATION_SEARCH_OPERATION}` as const;

export type ApplicationSearchParameters = paths[typeof APPLICATION_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type ApplicationSearchBody =
  paths[typeof APPLICATION_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type ApplicationSearchResponse =
  paths[typeof APPLICATION_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useApplicationSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<ApplicationSearchParameters, ApplicationSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    ApplicationSearchResponse,
    Error,
    ApplicationSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<ApplicationSearchParameters, ApplicationSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      APPLICATION_SEARCH_RESOURCE,
      APPLICATION_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<ApplicationSearchResponse>(
        APPLICATION_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
